import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { ProjectsService } from 'src/app/akita/project/state/projects.service';
import { SharedProjectService } from 'src/app/core/service/api/shared-project.service';
import { CachedService } from 'src/app/core/service/common/cached.service';
import { ConfirmationDialogComponent } from 'src/app/shared/components/modal/confirmation-dialog/confirmation-dialog.component';
import { NewEntityModal } from 'src/app/core/util/abstract/new-entity-modal';
import { modalConfig, ModalEnum } from 'src/app/core/util/modalConfig';
import { showFeedbackSaved } from 'src/app/core/util/notification';
import { Globals } from 'src/app/globals';
import { TranslocoService } from '@jsverse/transloco';

export interface ProjectShareData {
  source_project_id: number;
  target_project_id: number;
  shared_level: string;
  status: string;
  modules: any[];
  users: any[];
}

@Component({
  selector: 'app-project-share',
  templateUrl: './project-share.component.html',
  styleUrl: './project-share.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectShareComponent extends NewEntityModal {
  modules: any[];
  users: any[];
  isSubmitting = signal(false);
  isLoading = signal(true);
  connection_id: number;
  declare public entity: ProjectShareData;
  connectionDetails: any;
  isTargetSide: boolean = false;
  sourceName: string ;
  options: any[] = [
    {
      label: 'shared_projects.enable_chat', type: 'chat'
    },
    {
      label: 'shared_projects.enable_read',
      description:
        'De ontvangende partij mag wel informatie verrijken binnen het eigen project. Dit heeft geen invloed op jouw projectinformatie.',
      type: 'read'
    },
  ];

  constructor(public _rout: Router, @Inject(MAT_DIALOG_DATA) public data: any, public dialogRef: MatDialogRef<ProjectShareComponent>, public globals: Globals, private projectsService: ProjectsService, private _cachedService: CachedService, private sharedProjectService: SharedProjectService, private dialog: MatDialog, private translocoService: TranslocoService) {
    super(data, dialogRef, globals);
  }
  
  ngOnInit(): void {
    this.initializeEntity();

    const projectName = this._rout.url.split("/")[1];
    const requests = [
      this.projectsService.getDialogItems(),
      this.projectsService.getProjectDataByCode(projectName),
    ];
    if (this.data?.project?.shared_id) {
      requests.push(this.sharedProjectService.getProjectDetail(this.data.project.shared_id));
    }
    forkJoin(requests).subscribe({
      next: (responses: any) => {
        this.modules = this.filterEnabledModules(responses[0]);
        this.users = responses[1]?.users ?? [];
        this.entity.users = this.users.filter(user => 
          user.scope.data.some(data => 
            data.roles.some(role => role.name === "Project Admin")
          ));
        if (this.data?.project?.shared_id) {
          const projectDetail: any = responses[2];
          this.sourceName = projectDetail.source_project.name
          this.patchEntityFromDetail(projectDetail);
          // Only show "managed by source" when an active accepted connection exists.
          // Cancelled/rejected connections keep a shared_id for re-request but must not lock the form.
          this.isTargetSide = projectDetail.side === 'target' && projectDetail.status === 'accepted';
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      }
    });
  }

  public filterEnabledModules(allModules: any){
    let modules = [];
    allModules.forEach(module => {
      const moduleId = module.module_id;
      if (this.globals.project && this.globals.project.customers && this.globals.project.customers.license_type === 'enterprise') {
        if ( moduleId === 'stakeholders' || moduleId === 'contactpersons' || moduleId === 'requirements' || moduleId === 'requirements_by' || moduleId === 'issues' || moduleId === 'tasks' || moduleId === 'contactmoments' || moduleId === 'documents') {
          modules.push(module);
        }
        if (this.globals.modules.some(module => module.module_id.includes('communication'))) {
          if (moduleId === 'activities' || moduleId === 'contents' || moduleId === 'complaints') {
            modules.push(module);
          }
        }
        if (this.globals.modules.some(module => module.module_id.includes('permits') || module.module_id.includes('permit')) && (moduleId === 'permits' || moduleId === 'permit')) {
          modules.push(module);
        }
      }
      if (this.globals.project && this.globals.project.customers && this.globals.project.customers.license_type === 'starter') {
        if (this.globals.modules.some(module => module.module_id.includes(moduleId))) {
          modules.push(module);
        }
        if ((moduleId==='contents' || moduleId==='activities') && this.globals.modules.some(module => module.module_id.includes('calendar'))) {
          modules.push(module);
        }
        if ((moduleId==='permits' || moduleId==='permit') && this.globals.modules.some(module => module.module_id.includes('permit') || module.module_id.includes('permits'))) {
          modules.push(module);
        }
      }
    });
    return modules;
  }

  protected initializeEntity(): void {
    this.entity = {
      source_project_id: this.globals.project?.projects_id ? this.globals.project.projects_id : null,
      target_project_id: this.data?.project?.id ? this.data.project.id : null,
      status: "requested",
      shared_level: 'chat',
      modules: [],
      users: [],
    }
  }

    private patchEntityFromDetail(detail: any): void {
      this.connection_id = detail.id;
      
      const sharedLevel = this.getSharedLevelFromDetail(detail);
      const allModules = this.mergeModules(detail);
      const allUsers = this.mergeUsers(detail);

      // Update connectionDetails with the metadata-aware shared_level
      this.connectionDetails = {
        ...detail,
        shared_level: sharedLevel
      };

      this.entity = {
        ...this.entity,
        modules: allModules,
        users: allUsers,
        shared_level: sharedLevel,
        status: detail.status,
        source_project_id: detail.source_project_id,
        target_project_id: detail.target_project_id,
      };
    }

    private getSharedLevelFromDetail(detail: any): string {
      return detail.metadata?.shared_level || detail.shared_level || 'chat';
    }

    private getAddedItems(detail: any, key: string): any[] {
      return detail[key] || detail.metadata?.[key] || [];
    }

    private resolveModule(moduleItem: any): any | null {
      // If it's already a full module object with module_id, return as is
      if (typeof moduleItem === 'object' && moduleItem.module_id) {
        return moduleItem;
      }

      // If it's a number or object with just an id, try to resolve from available modules
      const moduleId = typeof moduleItem === 'number' ? moduleItem : moduleItem?.id;
      if (moduleId && this.modules) {
        const foundModule = this.modules.find((m: any) => m.id === moduleId || m.module_id === moduleId);
        if (foundModule) {
          return foundModule;
        }
      }

      // If it's an object but we couldn't resolve it, return it anyway
      if (typeof moduleItem === 'object' && moduleItem.id) {
        return moduleItem;
      }

      return null;
    }

    private resolveUser(userItem: any): any | null {
      // If it's already a full user object, return as is
      if (typeof userItem === 'object' && userItem.id && userItem.name) {
        return userItem;
      }

      // If it's a number or object with just an id, try to resolve from available users
      const userId = typeof userItem === 'number' ? userItem : userItem?.id;
      if (userId && this.users) {
        const foundUser = this.users.find((u: any) => u.id === userId);
        if (foundUser) {
          return foundUser;
        }
      }

      // If it's an object but we couldn't resolve it, return it anyway
      if (typeof userItem === 'object' && userItem.id) {
        return userItem;
      }

      return null;
    }

    private getModuleId(module: any): string | number | null {
      return module?.module_id || module?.id || null;
    }

    private moduleExists(modules: any[], moduleId: string | number): boolean {
      return modules.some((m: any) => this.getModuleId(m) === moduleId);
    }

    private userExists(users: any[], userId: number): boolean {
      return users.some((u: any) => u.id === userId);
    }

    private mergeModules(detail: any): any[] {
      const baseModules = [...(detail.modules || [])];
      const modulesAdded = this.getAddedItems(detail, 'modules_added');

      if (!Array.isArray(modulesAdded) || modulesAdded.length === 0) {
        return baseModules.map((module: any) => ({
          ...module,
          modules_added: true,
          users_added: true
        }));
      }

      const mergedModules = [...baseModules];

      modulesAdded.forEach((moduleItem: any) => {
        const resolvedModule = this.resolveModule(moduleItem);
        if (!resolvedModule) {
          return;
        }

        const moduleId = this.getModuleId(resolvedModule);
        if (moduleId && !this.moduleExists(mergedModules, moduleId)) {
          mergedModules.push(resolvedModule);
        }
      });

      return mergedModules.map((module: any) => ({
        ...module,
        modules_added: true,
        users_added: true
      }));
    }

    private mergeUsers(detail: any): any[] {
      const baseUsers = [...(detail.users || [])];
      const usersAdded = this.getAddedItems(detail, 'users_added');

      if (!Array.isArray(usersAdded) || usersAdded.length === 0) {
        return baseUsers;
      }

      const mergedUsers = [...baseUsers];

      usersAdded.forEach((userItem: any) => {
        const resolvedUser = this.resolveUser(userItem);
        if (!resolvedUser) {
          return;
        }

        if (resolvedUser.id && !this.userExists(mergedUsers, resolvedUser.id)) {
          mergedUsers.push(resolvedUser);
        }
      });

      return mergedUsers;
    }

  protected submit(): void {
    // Check if there are any changes before showing confirmation dialog
    if (!this.hasChanges()) {
      // No changes, proceed directly without confirmation
      this.performSubmit();
      return;
    }

    // Open confirmation dialog only if there are changes
    const confirmationDialog = this.dialog.open(ConfirmationDialogComponent, modalConfig({
      data: {
        title: this.translocoService.translate('shared_projects.share_confirmation_dialog.title'),
        message: this.translocoService.translate('shared_projects.share_confirmation_dialog.body'),
        cancelText: this.translocoService.translate('shared_projects.share_confirmation_dialog.cancel'),
        confirmText: this.translocoService.translate('shared_projects.share_confirmation_dialog.disconnect'),
      },
      panelClass: 'customized-dialog-container',
    }, ModalEnum.ModalSmall));

    confirmationDialog.afterClosed().subscribe(result => {
      if (result) {
        this.performSubmit();
      }
    });
  }

  private hasChanges(): boolean {
    // If it's a new connection (no shared_id), there are always changes
    if (!this.data?.project?.shared_id || !this.connectionDetails || this.entity.status === 'cancelled') {
      return false;
    }

    // Compare shared_level
    if (this.entity.shared_level !== this.connectionDetails.shared_level) {
      return true;
    }

    // Compare modules arrays (compare by module_id)
    const currentModuleIds = (this.entity.modules || []).map((m: any) => m.module_id || m.id).sort();
    const originalModuleIds = (this.connectionDetails.modules || []).map((m: any) => m.module_id || m.id).sort();
    if (JSON.stringify(currentModuleIds) !== JSON.stringify(originalModuleIds)) {
      return true;
    }

    // Compare users arrays (compare by id)
    const currentUserIds = (this.entity.users || []).map((u: any) => u.id).sort();
    const originalUserIds = (this.connectionDetails.users || []).map((u: any) => u.id).sort();
    if (JSON.stringify(currentUserIds) !== JSON.stringify(originalUserIds)) {
      return true;
    }

    // No changes detected
    return false;
  }

  private performSubmit(): void {
    if (!this.data?.project?.shared_id) {    
      this.isSubmitting.set(true);
      this.sharedProjectService.createProjectConnection(this.entity).subscribe(()=>{
        this.isSubmitting.set(false);
        this.dialogRef.close();
        showFeedbackSaved();
      }) 
    } 
    if (this.data?.project?.shared_id) {
      this.isSubmitting.set(true);
    
      // if the status is rejected and user try to send again then change the status
      this.entity.status = this.entity.status === 'cancelled' ? 'requested' : this.entity.status; 
      // if shared_level is changed
      if ( this.entity.shared_level !== this.connectionDetails.shared_level ) {
        this.entity.modules = this.entity.shared_level === 'chat'? [] : this.entity.modules;
      }
      this.sharedProjectService.updateSharedProjectDetail(this.connection_id, this.entity).subscribe((res)=>{
        this.isSubmitting.set(false);
        this.dialogRef.close();
        showFeedbackSaved();
      })
    }
  }

  onClose(reOpenModal = false) {
    if (reOpenModal && this.data?.openPreviousModal) {
      this.dialogRef.close(true);
      return;
    }
    this.dialogRef.close();
  }

  updateEntity(){
    if (this.entity.shared_level==='chat') {
      // this.entity.modules = [];
      // this.entity.users = [];
    }
  }
}
